/**
 * LayersPanel — Inspector tab for CAD (DWG/DXF) drawings: shared layer
 * visibility (saved per drawing for the whole team), drawing units, layer
 * totals for the open page, split into sheets, export / save / print.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { cadInfo, cadUpdate, cadQuantities, cadSplit, cadDownload, cadAddPreset, cadRemovePreset, cadRemoveEdit } from "./api";
import { useTakeoff } from "./store";
import { INPUT, BTN_GHOST } from "./theme";
import { Eye, EyeOff, Layers, Scissors, Download, Printer, Sigma, Loader2, RefreshCw, Bookmark, X, Undo2, PenLine } from "lucide-react";

const fmt = (n, d = 1) => (Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: d }) : "—");

export default function LayersPanel({ drawing, pageNum, cad, onCadChange, cadLayer, onCadLayer, cadDraw, onCadDraw }) {
  const setDraw = (patch) => onCadDraw?.({ ...(cadDraw || {}), ...patch });
  const { refreshDrawings, viewer } = useTakeoff();
  const [q, setQ] = useState("");
  const [onlyPage, setOnlyPage] = useState(true);
  const [busy, setBusy] = useState("");
  const [quant, setQuant] = useState(null);
  const pending = useRef(null);
  const hidden = useMemo(() => new Set(cad?.hidden || []), [cad]);
  const status = cad?.status;

  const layers = useMemo(() => {
    const all = cad?.layers || [];
    const needle = q.trim().toLowerCase();
    return all.filter((l) => (!onlyPage || l.used.includes(pageNum)) && (!needle || l.name.toLowerCase().includes(needle)));
  }, [cad, q, onlyPage, pageNum]);

  // debounce visibility saves so rapid toggles trigger ONE re-render
  const saveHidden = (next) => {
    onCadChange?.({ ...cad, hidden: [...next].sort(), status: "rendering" });
    clearTimeout(pending.current);
    pending.current = setTimeout(async () => {
      try { onCadChange?.(await cadUpdate(drawing.drawing_id, { hidden: [...next] })); }
      catch (e) { toast.error(e?.response?.data?.detail || "Could not save layer visibility"); }
    }, 500);
  };
  const toggle = (name) => { const n = new Set(hidden); n.has(name) ? n.delete(name) : n.add(name); saveHidden(n); };
  const setAll = (hide) => { const n = new Set(hidden); layers.forEach((l) => (hide ? n.add(l.name) : n.delete(l.name))); saveHidden(n); };
  const isolate = (name) => { const n = new Set(hidden); (cad?.layers || []).forEach((l) => { if (l.name !== name) n.add(l.name); }); n.delete(name); saveHidden(n); };

  // named layer views (shared)
  const savePreset = async () => {
    const name = window.prompt("Name this layer view (e.g. Sanitary only):");
    if (!name || !name.trim()) return;
    try { const r = await cadAddPreset(drawing.drawing_id, name.trim(), [...hidden]); onCadChange?.({ ...cad, presets: r.presets }); toast.success(`Saved "${name.trim()}"`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save the preset"); }
  };
  const applyPreset = (p) => saveHidden(new Set(p.hidden));
  const removePreset = async (p) => {
    try { const r = await cadRemovePreset(drawing.drawing_id, p.id); onCadChange?.({ ...cad, presets: r.presets }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove the preset"); }
  };
  const pageEdits = (cad?.edits || {})[String(pageNum)] || [];
  const undoEdit = async () => {
    const last = pageEdits[pageEdits.length - 1];
    if (!last) return;
    try {
      await cadRemoveEdit(drawing.drawing_id, last.id, pageNum);
      onCadChange?.({ ...cad, status: "rendering", edits: { ...(cad.edits || {}), [String(pageNum)]: pageEdits.slice(0, -1) } });
      toast.success("Last CAD edit removed — redrawing the sheet…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not undo"); }
  };

  const setUnits = async (units) => {
    try { onCadChange?.(await cadUpdate(drawing.drawing_id, { units })); toast.success(`Drawing units: ${units}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change units"); }
  };

  const loadQuant = async () => {
    setBusy("quant");
    try { setQuant(await cadQuantities(drawing.drawing_id, pageNum)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Quantities are not ready yet"); }
    finally { setBusy(""); }
  };
  useEffect(() => { setQuant(null); }, [drawing?.drawing_id, pageNum]);

  const split = async () => {
    setBusy("split");
    try {
      const r = await cadSplit(drawing.drawing_id);
      toast.success(`Split into ${r.created.length} sheet${r.created.length === 1 ? "" : "s"} — rendering now`);
      refreshDrawings?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not split this drawing"); }
    finally { setBusy(""); }
  };

  const download = async (kind) => {
    setBusy(kind);
    try {
      const { url, filename } = await cadDownload(drawing.drawing_id, kind);
      const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e?.response?.data?.detail || `Could not download the ${kind.toUpperCase()}`); }
    finally { setBusy(""); }
  };

  const print = async () => {
    setBusy("print");
    try {
      const { url } = await cadDownload(drawing.drawing_id, "pdf");
      // the browser print dialog carries "Save as PDF"
      const w = window.open(url, "_blank");
      if (!w) { toast.message("Allow pop-ups to print, or use Export PDF."); return; }
      w.addEventListener("load", () => setTimeout(() => w.print(), 400));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open the print view"); }
    finally { setBusy(""); }
  };

  if (!cad) return null;
  const page = cad.pages?.find((p) => p.page === pageNum);
  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid="layers-panel">
      <div className="shrink-0 border-b border-slate-200 p-3">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-[#1a3fd1]" />
          <p className="text-xs font-semibold text-slate-800">{page?.layout || "CAD"} · {cad.layers?.length || 0} layers</p>
          {status && status !== "ready" && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid="layers-status">
              <Loader2 className="h-3 w-3 animate-spin" />{status === "rendering" ? "Re-rendering…" : status}
            </span>
          )}
        </div>
        <p className="mt-1 text-[10px] text-slate-500">
          Layer visibility is shared with the team. Drawing units: {" "}
          <select value={cad.units || "m"} onChange={(e) => setUnits(e.target.value)} disabled={viewer} className="rounded border border-slate-300 bg-white px-1 py-0.5 text-[10px]" data-testid="cad-units-select">
            {["m", "mm", "cm", "ft", "in"].map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          {cad.drawing_units === "unitless" && <span className="ml-1 text-amber-600">(file has no units — confirm)</span>}
        </p>
        <div className="mt-2 flex flex-wrap gap-1">
          <button onClick={split} disabled={!!busy || viewer || !!cad.parent} className={`${BTN_GHOST} text-[10px]`} data-testid="cad-split-btn" title="One file per sheet (Model, G-1, LG-1…)"><Scissors className="mr-1 inline h-3 w-3" />Split into sheets</button>
          <button onClick={() => download("pdf")} disabled={!!busy} className={`${BTN_GHOST} text-[10px]`} data-testid="cad-export-pdf-btn"><Download className="mr-1 inline h-3 w-3" />Export PDF</button>
          <button onClick={() => download("dwg")} disabled={!!busy} className={`${BTN_GHOST} text-[10px]`} data-testid="cad-save-dwg-btn"><Download className="mr-1 inline h-3 w-3" />Save as DWG</button>
          <button onClick={() => download("dxf")} disabled={!!busy} className={`${BTN_GHOST} text-[10px]`} data-testid="cad-save-dxf-btn"><Download className="mr-1 inline h-3 w-3" />Save as DXF</button>
          <button onClick={print} disabled={!!busy} className={`${BTN_GHOST} text-[10px]`} data-testid="cad-print-btn" title="Print — the dialog includes Save to PDF"><Printer className="mr-1 inline h-3 w-3" />Print</button>
        </div>
      </div>

      {/* layer views + drawing tools */}
      <div className="shrink-0 space-y-2 border-b border-slate-200 p-3" data-testid="layer-presets">
        <div className="flex flex-wrap items-center gap-1">
          <Bookmark className="h-3.5 w-3.5 text-slate-500" />
          {(cad.presets || []).map((p) => (
            <span key={p.id} className="inline-flex items-center overflow-hidden rounded-full border border-slate-300 bg-white text-[10px]" data-testid={`layer-preset-${p.id}`}>
              <button onClick={() => applyPreset(p)} disabled={viewer} className="px-2 py-0.5 font-semibold text-slate-700 hover:bg-slate-100" title={`${p.hidden.length} layers hidden`} data-testid={`layer-preset-apply-${p.id}`}>{p.name}</button>
              {!viewer && <button onClick={() => removePreset(p)} className="px-1 text-slate-400 hover:text-red-600" aria-label={`Remove ${p.name}`} data-testid={`layer-preset-remove-${p.id}`}><X className="h-3 w-3" /></button>}
            </span>
          ))}
          <button onClick={() => applyPreset({ hidden: [] })} disabled={viewer} className="rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[10px] text-slate-500 hover:bg-slate-100" data-testid="layer-preset-all">All layers</button>
          {!viewer && <button onClick={savePreset} className="rounded-full border border-[#1a3fd1]/40 px-2 py-0.5 text-[10px] font-semibold text-[#1a3fd1] hover:bg-[#1a3fd1]/10" data-testid="layer-preset-save">+ Save current view</button>}
        </div>
        {!viewer && (
          <div className="flex items-center gap-2 text-[10px] text-slate-600" data-testid="cad-draw-row">
            <PenLine className="h-3.5 w-3.5 text-slate-500" />
            <span>Draw on</span>
            <LayerPicker layers={cad.layers || []} value={cadLayer || "0"} onChange={onCadLayer} />
            <button onClick={undoEdit} disabled={!pageEdits.length} className="inline-flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40" title="Remove the last CAD edit on this page" data-testid="cad-undo-edit">
              <Undo2 className="h-3 w-3" />{pageEdits.length} edit{pageEdits.length === 1 ? "" : "s"}
            </button>
          </div>
        )}
        {!viewer && cadDraw && (
          <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] text-slate-600" data-testid="cad-draw-settings">
            <label className="flex items-center gap-1">Dim units
              <select value={cadDraw.dimFormat} onChange={(e) => setDraw({ dimFormat: e.target.value })} className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-1 py-0.5" data-testid="cad-dim-format">
                <option value="ftin">Feet-inches (12'-6")</option>
                <option value="ft">Decimal feet (12.50')</option>
                <option value="m">Metres (3.81 m)</option>
                <option value="mm">Millimetres (3810 mm)</option>
              </select>
            </label>
            <label className="flex items-center gap-1">Text size
              <select value={cadDraw.dimHeight} onChange={(e) => setDraw({ dimHeight: Number(e.target.value) })} className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-1 py-0.5" data-testid="cad-dim-height">
                {[1.5, 2.5, 3.5, 5, 7].map((h) => <option key={h} value={h}>{h} mm</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1">Grid
              <select value={cadDraw.grid} onChange={(e) => setDraw({ grid: Number(e.target.value) })} className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-1 py-0.5" data-testid="cad-grid">
                <option value={0}>Off</option><option value={5}>Fine</option><option value={10}>Medium</option><option value={25}>Coarse</option>
              </select>
            </label>
            <label className="flex items-center gap-1" title="Lock new lines and dimensions to horizontal / vertical">
              <input type="checkbox" checked={!!cadDraw.ortho} onChange={(e) => setDraw({ ortho: e.target.checked })} data-testid="cad-ortho" /> Ortho lock
            </label>
            <label className="col-span-2 flex items-center gap-1">Symbol
              <select value={cadDraw.symbol} onChange={(e) => setDraw({ symbol: e.target.value })} className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-1 py-0.5" data-testid="cad-symbol">
                <option value="manhole">Manhole (MH)</option><option value="catchbasin">Catch basin (CB)</option><option value="hydrant">Hydrant</option>
                <option value="valve">Valve</option><option value="cleanout">Cleanout (CO)</option><option value="lightpole">Light pole</option>
              </select>
              <span className="text-slate-400">— stamp with the symbol tool</span>
            </label>
          </div>
        )}
      </div>

      <div className="shrink-0 space-y-2 border-b border-slate-200 p-3">
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search layers" className={`${INPUT} flex-1 py-1 text-xs`} data-testid="layers-search" />
          <label className="flex items-center gap-1 text-[10px] text-slate-500"><input type="checkbox" checked={onlyPage} onChange={(e) => setOnlyPage(e.target.checked)} data-testid="layers-only-page" />this page</label>
        </div>
        <div className="flex gap-2 text-[10px]">
          <button onClick={() => setAll(false)} disabled={viewer} className="font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800" data-testid="layers-show-all">Show all</button>
          <button onClick={() => setAll(true)} disabled={viewer} className="font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800" data-testid="layers-hide-all">Hide all</button>
          <span className="ml-auto text-slate-400">{hidden.size} hidden</span>
        </div>
      </div>

      <ul className="min-h-0 flex-1 overflow-y-auto" data-testid="layers-list">
        {layers.map((l) => {
          const off = hidden.has(l.name);
          return (
            <li key={l.name} className={`group flex items-center gap-2 border-b border-slate-100 px-3 py-1.5 text-xs ${off ? "text-slate-400" : "text-slate-800"}`} data-testid={`layer-row-${l.name}`}>
              <button onClick={() => toggle(l.name)} disabled={viewer} aria-pressed={!off} title={off ? "Show layer" : "Hide layer"} className="shrink-0 text-slate-500 hover:text-slate-900" data-testid={`layer-toggle-${l.name}`}>
                {off ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
              <span className="h-3 w-3 shrink-0 rounded-sm border border-black/10" style={{ background: /^#f{6}$/i.test(l.color) ? "#111" : l.color }} title={l.color} />
              <span className="min-w-0 flex-1 truncate" title={l.name}>{l.name}</span>
              {l.frozen && <span className="text-[9px] uppercase text-slate-400">frozen</span>}
              <button onClick={() => isolate(l.name)} disabled={viewer} className="hidden shrink-0 text-[9px] font-semibold uppercase tracking-wider text-[#1a3fd1] group-hover:inline" data-testid={`layer-isolate-${l.name}`}>Isolate</button>
            </li>
          );
        })}
        {layers.length === 0 && <li className="p-4 text-center text-xs text-slate-400">No layers match.</li>}
      </ul>

      <div className="shrink-0 border-t border-slate-200 p-3">
        <button onClick={loadQuant} disabled={busy === "quant"} className={`${BTN_GHOST} w-full text-[10px]`} data-testid="cad-quantities-btn">
          {busy === "quant" ? <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> : quant ? <RefreshCw className="mr-1 inline h-3 w-3" /> : <Sigma className="mr-1 inline h-3 w-3" />}
          Layer quantities — this page
        </button>
        {quant && (
          <div className="mt-2 max-h-56 overflow-y-auto rounded border border-slate-200" data-testid="cad-quantities-table">
            <table className="w-full text-[10px]">
              <thead className="sticky top-0 bg-slate-50 text-left text-slate-500"><tr><th className="px-2 py-1">Layer</th><th className="px-1 py-1 text-right">LF</th><th className="px-1 py-1 text-right">SF</th><th className="px-1 py-1 text-right">Blocks</th></tr></thead>
              <tbody>
                {quant.layers.filter((r) => r.length_ft > 0.05 || r.area_sf > 0.05 || r.blocks).map((r) => (
                  <tr key={r.layer} className="border-t border-slate-100" data-testid={`cad-qty-${r.layer}`}>
                    <td className="max-w-[150px] truncate px-2 py-1" title={r.layer}>{r.layer}</td>
                    <td className="px-1 py-1 text-right tabular-nums">{fmt(r.length_ft)}</td>
                    <td className="px-1 py-1 text-right tabular-nums">{fmt(r.area_sf, 0)}</td>
                    <td className="px-1 py-1 text-right tabular-nums">{r.blocks || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** Poll a CAD drawing's info while it converts / re-renders. */
export function useCadInfo(drawing) {
  const [cad, setCad] = useState(null);
  const id = drawing?.drawing_id;
  const isCad = !!drawing?.cad;
  const fetchInfo = async (alive) => {
    try {
      const info = await cadInfo(id);
      if (alive.current) setCad(info);
    } catch { /* not a CAD drawing (yet) */ }
  };
  const aliveRef = useRef({ current: true });
  const prevId = useRef(null);
  useEffect(() => {
    aliveRef.current = { current: true };
    // only blank the panel when a DIFFERENT drawing opens — a re-render of
    // the same drawing keeps the layer list on screen while it refreshes
    if (prevId.current !== id) { setCad(null); prevId.current = id; }
    if (id && isCad) fetchInfo(aliveRef.current);
    return () => { aliveRef.current.current = false; };
  }, [id, isCad, drawing?.cad?.render_version]); // eslint-disable-line
  // keep polling while the server is converting / re-rendering (also after a
  // layer toggle from the panel, which flips status to "rendering" locally)
  const busy = cad?.status === "processing" || cad?.status === "rendering";
  useEffect(() => {
    if (!busy || !id) return undefined;
    const t = setTimeout(() => fetchInfo(aliveRef.current), 2500);
    return () => clearTimeout(t);
  }, [busy, id, cad]); // eslint-disable-line
  return [cad, setCad];
}

/** Searchable "draw on layer" picker — type to filter 400+ layers to one. */
function LayerPicker({ layers, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const needle = q.trim().toLowerCase();
  const shown = needle ? layers.filter((l) => l.name.toLowerCase().includes(needle)).slice(0, 60) : layers.slice(0, 60);
  return (
    <div ref={ref} className="relative min-w-0 flex-1" data-testid="cad-draw-layer">
      <button type="button" onClick={() => { setOpen((o) => !o); setQ(""); }} className="flex w-full items-center gap-1 truncate rounded border border-slate-300 bg-white px-1.5 py-0.5 text-left text-[10px] hover:border-[#1a3fd1]" data-testid="cad-draw-layer-button" title={value}>
        <span className="min-w-0 flex-1 truncate">{value}</span>
        <span className="text-slate-400">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-1 w-56 rounded-lg border border-slate-300 bg-white shadow-xl">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search layers…" className="w-full border-b border-slate-200 px-2 py-1 text-[11px] outline-none" data-testid="cad-draw-layer-search" />
          <ul className="max-h-56 overflow-y-auto py-1">
            {shown.map((l) => (
              <li key={l.name}>
                <button type="button" onClick={() => { onChange?.(l.name); setOpen(false); }} className={`flex w-full items-center gap-2 px-2 py-1 text-left text-[11px] hover:bg-slate-100 ${l.name === value ? "font-bold text-[#1a3fd1]" : "text-slate-700"}`} data-testid={`cad-draw-layer-option-${l.name}`}>
                  <span className="h-2.5 w-2.5 shrink-0 rounded-sm border border-black/10" style={{ background: /^#f{6}$/i.test(l.color) ? "#111" : l.color }} />
                  <span className="min-w-0 flex-1 truncate" title={l.name}>{l.name}</span>
                </button>
              </li>
            ))}
            {shown.length === 0 && <li className="px-2 py-2 text-center text-[10px] text-slate-400">No match</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
